import { Box, Container, Grid, Typography } from '@material-ui/core'
import { ReportProblemOutlined } from '@material-ui/icons'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { Skeleton } from '@material-ui/lab'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import AlertDialog from '@/components/alert-dialog'
import Button from '@/components/button'
import { useCheckRegions } from '@/hooks/queries/use-check-regions'
import { useAuth } from '@/hooks/use-auth'
import { useCompanies } from '@/hooks/use-companies'
import { routes } from '@/routes/config'
import { getDiagnosisRegisterAcceptanceTerm } from '@/services/diagnosis-register-acceptance-term'
import { Culture } from '@/types/culture'
import { ImageType } from '@/types/image-types'
import { Treatment } from '@/types/treatment'

import { useDiagnosisRegistrationStore } from '../../contexts/diagnosis-registration-store-context'
import { AcceptanceTermModal } from './components/acceptance-term-modal'
import DemandResume from './components/demand-resume'
import Map from './components/map'
import { useStyles } from './styles'

export const Resume = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { permissions } = useCompanies()
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isAcceptanceTermOpen, setIsAcceptanceTermOpen] = useState(false)

  const {
    registerData,
    selectFilesData,
    plantingFailureData,
    setPolygonsData,
  } = useDiagnosisRegistrationStore((state) => ({
    registerData: state.registerData,
    selectFilesData: state.selectFilesData,
    plantingFailureData: state.plantingFailureData,
    setPolygonsData: state.setPolygonsData,
  }))

  const hasData = !!(registerData && selectFilesData)
  const isWeedAccess = permissions?.WeedAccess

  const {
    isFetching: isFetchingPolygons,
    data: polygons,
    refetch: identifyPolygons,
  } = useCheckRegions(
    {
      region_id: registerData?.region?.id!,
      project_id: registerData?.project.id!,
      geojson: selectFilesData?.perimeter.results.geojson!,
    },
    { enabled: hasData && isWeedAccess },
  )

  useEffect(() => {
    if (!polygons?.unmatching) return

    setIsAlertOpen(true)
  }, [polygons])

  if (!hasData) {
    return <Navigate to={routes.Demands('register')} replace />
  }

  function getImageSize() {
    if (selectFilesData?.mosaic?.input.at(0)?.size)
      return selectFilesData.mosaic?.input.at(0)?.size

    const thermalSize = selectFilesData?.thermal?.results.images_size ?? 0
    const rgbSize = Number(selectFilesData?.rgb?.results.images_size)

    return rgbSize + thermalSize
  }

  function getImageCount() {
    const thermalCount = selectFilesData?.thermal?.results.image_count ?? 0

    const rgbCount = selectFilesData?.rgb?.results.image_count ?? 0

    return thermalCount + rgbCount
  }

  const formData = {
    treatment: registerData.treatment as Treatment,
    culture: registerData.culture as Culture,
    image_type: registerData.image_type as ImageType,
    project: registerData.project.name,
    region: registerData.region?.name ?? '-',
    demand_name: registerData.demand_name,
    flight_date: registerData.flight_date,
    planting_type: plantingFailureData?.planting_type,
    planting_space: plantingFailureData?.planting_space,
    cane_cycle_type: plantingFailureData?.cane_cycle_type,
    sponsor: user.name,
    area: selectFilesData.perimeter.results.area,
    image_size: getImageSize(),
    image_count: getImageCount(),
    polygons,
  }

  function handleNext() {
    const isAcceptanceTerm = getDiagnosisRegisterAcceptanceTerm()

    if (!isAcceptanceTerm) {
      return setIsAcceptanceTermOpen(true)
    }

    handleSubmit()
  }

  function handleSubmit() {
    if (polygons) {
      setPolygonsData(polygons.matching)
    }
    navigate(routes.Demands('upload-files'))
  }

  function navigateToProjectEdit() {
    const projectID = registerData?.project.id
    window.open(routes.ProjectEdit(projectID), '_blank')
    setIsAlertOpen(false)
  }

  return (
    <Container className={classes.main}>
      <Box>
        <Typography variant={'h1'}>Informações do diagnóstico</Typography>
        <Typography variant={'h4'}>
          confirme se os campos abaixo foram preenchidos da forma correta
        </Typography>

        <Grid container spacing={2} className={classes.container}>
          <Grid item xs={7}>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              className={classes.button}
            >
              <Typography variant='subtitle2'>Resumo</Typography>
              {isWeedAccess && (
                <Button
                  labelOnly
                  onClick={() => identifyPolygons()}
                  loading={isFetchingPolygons}
                >
                  <AutorenewIcon />
                  <Typography variant='subtitle2'>
                    Identificar polígonos novamente
                  </Typography>
                </Button>
              )}
            </Box>
            {isWeedAccess && isFetchingPolygons ? (
              <Skeleton width={'100%'} height={380} variant='rect' />
            ) : (
              <DemandResume formData={formData} />
            )}
          </Grid>

          <Grid item xs={5}>
            <Typography variant='subtitle2'>Área a ser processada</Typography>
            <Map
              perimeter={selectFilesData.perimeter.results.geojson}
              mosaic={selectFilesData.mosaic?.input[0]}
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.buttonContainer}>
        <Button outlined onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button disabled={isWeedAccess && !polygons} onClick={handleNext}>
          Avançar
        </Button>
      </Box>
      <AlertDialog
        variant='error'
        icon={<ReportProblemOutlined />}
        title='Não identificamos todos os polígonos'
        subtitle='Não conseguimos identificar um ou mais polígonos de sua propriedade, atualize seu projeto para que possamos identificá-los.'
        cancel='Seguir sem identificar'
        confirm='Atualizar meu projeto'
        items={[
          'Abriremos uma nova aba para você atualizar os polígonos de sua propriedade, após atualizar clique no botão “Identificar polígonos novamente”',
        ]}
        open={isAlertOpen}
        handleConfirm={navigateToProjectEdit}
        handleCancel={() => {
          setIsAlertOpen(false)
        }}
      />

      <AcceptanceTermModal
        open={isAcceptanceTermOpen}
        onClose={() => setIsAcceptanceTermOpen(false)}
        onSubmit={handleSubmit}
      />
    </Container>
  )
}
